import {
  createContext,
  useCallback,
  useContext,
  useRef,
  useSyncExternalStore,
} from 'react'
import type { EditorStore } from './EditorStore'

export const EditorStoreContext = createContext<EditorStore | null>(null)

export function useEditorStore(): EditorStore {
  const store = useContext(EditorStoreContext)
  if (!store) {
    throw new Error('useEditorStore must be used within EditorStoreProvider')
  }
  return store
}

function shallowEqual(a: unknown, b: unknown): boolean {
  if (Object.is(a, b)) return true
  if (
    typeof a !== 'object' ||
    a === null ||
    typeof b !== 'object' ||
    b === null
  )
    return false
  if (Array.isArray(a)) {
    if (!Array.isArray(b) || a.length !== b.length) return false
    for (let i = 0; i < a.length; i++) {
      if (!Object.is(a[i], b[i])) return false
    }
    return true
  }
  const keysA = Object.keys(a)
  const keysB = Object.keys(b)
  if (keysA.length !== keysB.length) return false
  for (const key of keysA) {
    if (
      !Object.is(
        (a as Record<string, unknown>)[key],
        (b as Record<string, unknown>)[key],
      )
    )
      return false
  }
  return true
}

/**
 * Subscribe to a derived value from the store, similar to tldraw's `useValue`.
 * Re-renders only when the selected value changes (shallow equality).
 */
export function useEditorValue<T>(selector: (store: EditorStore) => T): T {
  const store = useEditorStore()
  const prevRef = useRef<T>(null)
  const selectorRef = useRef(selector)
  selectorRef.current = selector

  const subscribe = useCallback(
    (callback: () => void) => store.subscribe(callback),
    [store],
  )

  const getSnapshot = useCallback(() => {
    const next = selectorRef.current(store)
    if (shallowEqual(prevRef.current, next)) return prevRef.current as T
    prevRef.current = next
    return next
  }, [store])

  return useSyncExternalStore(subscribe, getSnapshot)
}
